import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../layout/Header";
import Footer from "../layout/Footer";
import { useSEO } from "../hooks/useSEO";
import { 
  getPost, 
  listPosts, 
  postThumbnail, 
  formatBlogDate, 
  postExcerpt, 
  type BlogPost as Post 
} from "../services/blogger";
import "./Blog.css";

const parseContent = (html: string) => {
  if (typeof window === "undefined") return { imageUrl: null, cleanContent: html };
  
  const doc = new DOMParser().parseFromString(html, "text/html");
  
  // 1. Extract and remove the first image for the hero section
  const firstImg = doc.querySelector("img");
  let imageUrl = null;
  
  if (firstImg) {
    imageUrl = firstImg.src;
    const parent = firstImg.parentElement;
    if (parent && parent.tagName.toLowerCase() === "a") {
      parent.remove();
    } else {
      firstImg.remove();
    }
  }
  
  // 2. Fix tables to ensure mobile responsiveness
  const tables = doc.querySelectorAll("table");
  tables.forEach((table) => {
    // Create a responsive wrapper
    const wrapper = doc.createElement("div");
    wrapper.className = "w-full overflow-x-auto overflow-y-hidden my-6 rounded-sm";
    
    // Remove rigid width attributes injected by Blogger
    table.removeAttribute("width");
    table.style.width = "100%";
    table.style.minWidth = "600px"; // Forces the table to scroll rather than squash text on mobile
    
    // Wrap the table
    table.parentNode?.insertBefore(wrapper, table);
    wrapper.appendChild(table);
  });

  return { imageUrl, cleanContent: doc.body.innerHTML };
};

export default function BlogPost() {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();

  const [post, setPost] = useState<Post | null>(null);
  const [recentPosts, setRecentPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        if (!postId) throw new Error("Post not found.");
        
        // Fetch the current post and the recent posts list simultaneously
        const [postData, recentPostsData] = await Promise.all([
          getPost(postId),
          listPosts()
        ]);

        if (active) {
          setPost(postData);
          
          // Filter out the current post from the list and take the first 2 for the Further Reading section
          const filteredRecent = (recentPostsData.items || [])
            .filter((p) => p.id !== postId)
            .slice(0, 2);
            
          setRecentPosts(filteredRecent);
        }
      } catch (e: any) {
        if (active) setError(e?.message || "Couldn't load this post.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [postId]);

  const { imageUrl, cleanContent, readTime } = useMemo(() => {
    if (!post?.content) return { imageUrl: null, cleanContent: "", readTime: 1 };
    const parsed = parseContent(post.content);
    const wordCount = parsed.cleanContent.replace(/<[^>]*>?/gm, '').split(/\s+/).length;
    const estimatedTime = Math.max(1, Math.ceil(wordCount / 200));
    
    return { ...parsed, readTime: estimatedTime };
  }, [post?.content]);

  // SEO uses the post once loaded; noindex the shell while loading/erroring.
  useSEO({
    title: post ? `${post.title} — Vastavik Blog` : "Blog — Vastavik",
    description: post ? postExcerpt(post, 155) : "Vastavik blog post.",
    path: `/blog/${postId ?? ""}`,
    noindex: !post,
  });

  return (
    <div className="min-h-screen bg-white text-black flex flex-col">
      <Header />

      <main className="flex-1 w-full">
        <div className="mx-auto w-full max-w-[820px] px-5 py-10 md:px-8 md:py-14">
          <button
            onClick={() => navigate("/blog")}
            className="mb-8 inline-flex items-center gap-1.5 text-[14px] font-medium text-black/60 transition hover:text-black"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M19 12H5M11 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Back to Blog
          </button>

          {loading ? (
            <div className="flex justify-center py-24">
              <div className="h-9 w-9 animate-spin rounded-full border-2 border-black/15 border-t-black" />
            </div>
          ) : error || !post ? (
            <div className="rounded-xl border border-black/10 bg-black/[0.02] p-10 text-center">
              <p className="text-[16px] font-medium">Post not available</p>
              <p className="mt-2 text-[14px] text-black/55">
                {error || "This post could not be found."}
              </p>
              <button
                onClick={() => navigate("/blog")}
                className="mt-5 rounded-lg border border-black/15 px-4 py-2 text-[14px] transition hover:bg-black/5"
              >
                Back to Blog
              </button>
            </div>
          ) : (
            <>
              <article className="max-w-full overflow-hidden">
                <header className="mb-6 flex flex-col items-start">
                  <h1
                    className="mb-4 text-[#000000] font-bold text-[40px] leading-tight md:text-[56px] md:leading-[70px] text-balance"
                    style={{
                      fontFamily: "'Caviar Dreams', sans-serif",
                      letterSpacing: "-1.12px",
                    }}
                  >
                    {post.title}
                  </h1>

                  <div
                    className="flex flex-wrap items-center gap-6 text-[#5F5E5E]"
                    style={{
                      fontFamily: "'Product Sans Light', sans-serif",
                      fontSize: "12px",
                      lineHeight: "12px",
                      letterSpacing: "0.6px",
                    }}
                  >
                    <div className="flex items-center gap-1.5">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                      <span>{formatBlogDate(post.published)}</span>
                    </div>

                    {post.author?.displayName && (
                      <div className="flex items-center gap-1.5">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                        <span>By {post.author.displayName}</span>
                      </div>
                    )}

                    <div className="flex items-center gap-1.5">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                      <span>{readTime} min read</span>
                    </div>
                  </div>
                </header>

                <hr className="mb-6 border-t border-[#E5E7EB]" />

                {imageUrl && (
                  <div className="mb-8 flex justify-center lg:justify-start">
                    <img
                      src={imageUrl}
                      alt={post.title}
                      className="w-full max-w-[704px] h-auto object-contain bg-[#F3F3F3] border border-[#7E7576]"
                    />
                  </div>
                )}

                <div
                  className="blog-content w-full"
                  dangerouslySetInnerHTML={{ __html: cleanContent }}
                />
              </article>

              {recentPosts.length > 0 && (
                <div className="mt-20 pt-8 border-t border-black/10">
                  <div className="flex items-center justify-between mb-8">
                    <h2 
                      className="text-[32px] font-bold text-black"
                      style={{ fontFamily: "'Caviar Dreams', sans-serif" }}
                    >
                      Further Reading
                    </h2>
                    <button 
                      onClick={() => navigate("/blog")}
                      className="flex items-center gap-2 text-[14px] uppercase tracking-wide font-medium text-black hover:opacity-70 transition-opacity"
                    >
                      VIEW LIBRARY
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {recentPosts.map((rp) => {
                      const thumb = postThumbnail(rp);
                      return (
                        <div 
                          key={rp.id}
                          onClick={() => navigate(`/blog/${rp.id}`)}
                          className="flex flex-col overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
                          style={{ backgroundColor: "#F9F9F9", border: "1px solid #7E7576" }}
                        >
                          <div className="h-[220px] w-full bg-[#E5E7EB] relative flex items-center justify-center overflow-hidden">
                            {thumb ? (
                              <img 
                                src={thumb} 
                                alt={rp.title} 
                                loading="lazy"
                                className="object-cover w-full h-full transition duration-500 hover:scale-105"
                              />
                            ) : (
                              <span className="font-maxwell-regular text-2xl uppercase tracking-wide text-black/20">
                                Vastavik
                              </span>
                            )}
                          </div>
                          <div className="p-6 flex flex-col flex-1">
                            <span className="text-[#5F5E5E] text-[14px] mb-2 font-light">
                              {formatBlogDate(rp.published)} 
                            </span>
                            <h3 
                              className="text-black mb-3 line-clamp-2"
                              style={{
                                fontFamily: "'Product Sans Medium', sans-serif",
                                fontSize: "18px",
                                lineHeight: "25.2px",
                              }}
                            >
                              {rp.title}
                            </h3>
                            <p className="text-[#5F5E5E] text-[15px] leading-relaxed line-clamp-3">
                              {postExcerpt(rp)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}